import { inject } from '@angular/core';
import { Type } from '@angular/core';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { LayoutService } from '../services/layout.service';

type ModuleRef<T> = Record<string, Type<T>>;

const RELATORIOS_V2_FLAG = 'ui.relatorios_v2';

/**
 * Garante flags carregadas antes do lazy route resolver.
 * Se a flag não estiver habilitada no cache, força refresh (evita cache antigo após toggle no admin).
 */
export async function useRelatoriosV2(flags: FeatureFlagsService): Promise<boolean> {
  const layout = inject(LayoutService);

  await flags.load();
  if (!flags.isEnabled(RELATORIOS_V2_FLAG)) {
    await flags.refresh();
  }
  if (!flags.isEnabled(RELATORIOS_V2_FLAG)) {
    return false;
  }

  return layout.useModernLayout();
}

/**
 * Carrega páginas de relatórios: builder/hub V2 quando `ui.relatorios_v2` está ativa
 * e o layout moderno está habilitado; caso contrário, usa ng-zorro clássico.
 */
export function loadRelatoriosPage<TClassic, TV2New = TClassic>(
  classicImport: () => Promise<ModuleRef<TClassic>>,
  exportName: string,
  newV2Import: () => Promise<ModuleRef<TV2New>>,
  newV2ExportName?: string,
): () => Promise<Type<TClassic>> {
  return async () => {
    const flags = inject(FeatureFlagsService);
    if (await useRelatoriosV2(flags)) {
      const mod = await newV2Import();
      const name = newV2ExportName ?? exportName;
      return mod[name] as unknown as Type<TClassic>;
    }
    const mod = await classicImport();
    return mod[exportName] as Type<TClassic>;
  };
}

/** Histórico em /relatorios — mesma lógica de flag. */
export function loadRelatoriosHistoryPage<TClassic, TV2New = TClassic>(
  classicImport: () => Promise<ModuleRef<TClassic>>,
  exportName: string,
  newV2Import: () => Promise<ModuleRef<TV2New>>,
  newV2ExportName?: string,
): () => Promise<Type<TClassic>> {
  return loadRelatoriosPage(classicImport, exportName, newV2Import, newV2ExportName);
}
